<template>
  <div class="letter-templates-container">
    <!-- Letters Grid -->
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      <div class="mt-4 text-gray-500 font-medium">Fetching letter templates...</div>
    </div>

    <div v-if="!loading && !filteredLetters.length"
      class="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300 mx-4">
      <v-icon size="80" color="grey lighten-2">mdi-file-document-edit-outline</v-icon>
      <h3 class="mt-4 text-xl font-semibold text-gray-700">
        {{ search ? 'No templates found matching your search' : 'No Letter Templates' }}
      </h3>
      <p class="text-gray-500 mt-2 max-w-sm mx-auto">
        {{
          search
            ? 'Try adjusting your search terms or create a new template.'
            : 'Create reusable letter templates that can be attached to your notifications.'
        }}
      </p>
      <v-btn v-if="!search" color="primary" @click="openCreateDialog" outlined class="mt-4 normal-case px-6">
        Create Your First Letter
      </v-btn>
    </div>

    <div v-else>
      <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3 px-4">
        <v-card v-for="letter in filteredLetters" :key="letter._id"
          class="letter-card overflow-hidden border border-gray-200 rounded-md elevation-0" hover>
          <div class="pa-4">
            <div class="d-flex justify-space-between align-center mb-3">
              <div class="d-flex align-center">
                <div :class="[getTypeStyle(letter.type).bg, 'p-2 rounded-lg mr-3 d-flex align-center']">
                  <v-icon :color="getTypeStyle(letter.type).color" size="24">{{
                    getTypeStyle(letter.type).icon
                    }}</v-icon>
                </div>
                <div class="d-flex flex-column">
                  <h3 class="text-md font-bold text-gray-800 leading-tight pr-2">
                    {{ letter.name }}
                  </h3>
                </div>
              </div>
              <div class="d-flex align-center">
                <v-tooltip bottom v-if="(letter.type || '').toUpperCase() === 'VOICE'">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" color="purple darken-2" class="mr-0"
                      @click="triggerTestCall(letter)">
                      <v-icon small>mdi-phone-in-talk</v-icon>
                    </v-btn>
                  </template>
                  <span>Test</span>
                </v-tooltip>
                <v-tooltip bottom v-if="(letter.type || '').toUpperCase() === 'EMAIL'">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" color="blue darken-4" class="mr-0"
                      @click="triggerTestEmail(letter)">
                      <v-icon small>mdi-send</v-icon>
                    </v-btn>
                  </template>
                  <span>Test</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" color="primary" class="mr-0"
                      @click="previewLetter(letter)">
                      <v-icon small>mdi-eye-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Preview</span>
                </v-tooltip>
                <v-menu offset-y left transition="slide-y-transition">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" color="grey darken-1">
                      <v-icon small>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense class="rounded-lg py-1 shadow-lg border border-gray-100">
                    <v-list-item @click="editLetter(letter)" v-iam-disabled="'notifications.templates.update'">
                      <v-list-item-icon class="mr-3"><v-icon small
                          color="primary">mdi-pencil-outline</v-icon></v-list-item-icon>
                      <v-list-item-title class="font-medium text-sm">Edit Template</v-list-item-title>
                    </v-list-item>
                    <template v-if="(letter.type || '').toUpperCase() === 'VOICE'">
                      <v-divider class="my-1"></v-divider>
                      <v-list-item @click="triggerTestCall(letter)">
                        <v-list-item-icon class="mr-3">
                          <v-icon small color="purple darken-2">mdi-phone-in-talk</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title class="font-medium text-sm text-purple-900">Test</v-list-item-title>
                      </v-list-item>
                    </template>
                    <template v-if="(letter.type || '').toUpperCase() === 'EMAIL'">
                      <v-divider class="my-1"></v-divider>
                      <v-list-item @click="triggerTestEmail(letter)">
                        <v-list-item-icon class="mr-3">
                          <v-icon small color="blue darken-4">mdi-email-send</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title class="font-medium text-sm text-blue-900">Test</v-list-item-title>
                      </v-list-item>
                    </template>
                    <template v-if="letter.type === 'WHATSAPP'">
                      <v-divider class="my-1"></v-divider>
                      <v-list-item @click="submitToMeta(letter)" :disabled="letter.whatsappStatus === 'APPROVED'"
                        v-iam-disabled="'notifications.templates.update'">
                        <v-list-item-icon class="mr-3">
                          <v-icon small color="green">mdi-whatsapp</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title class="font-medium text-sm">Send to Meta</v-list-item-title>
                      </v-list-item>
                      <!-- :disabled="!letter.whatsappTemplateId" -->
                      <v-list-item @click="syncMetaStatus(letter)" v-iam-disabled="'notifications.templates.update'">
                        <v-list-item-icon class="mr-3">
                          <v-icon small color="blue">mdi-sync</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title class="font-medium text-sm">Sync Status</v-list-item-title>
                      </v-list-item>
                    </template>
                    <template v-if="
                      letter.type === 'SMS' &&
                      (letter.smsTemplateId || (letter.metaData && letter.metaData.sqlTemplateId))
                    ">
                      <v-divider class="my-1"></v-divider>
                      <v-list-item @click="submitToQuickberry(letter)" :disabled="letter.smsStatus === 'APPROVED'"
                        v-iam-disabled="'notifications.templates.update'">
                        <v-list-item-icon class="mr-3">
                          <v-icon small color="amber darken-3">mdi-message-processing</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title class="font-medium text-sm">Send to Quickberry</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="syncQuickberryStatus(letter)" :disabled="!(
                          letter.smsTemplateId ||
                          (letter.metaData && letter.metaData.sqlTemplateId)
                        )
                        " v-iam-disabled="'notifications.templates.update'">
                        <v-list-item-icon class="mr-3">
                          <v-icon small color="blue">mdi-sync</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title class="font-medium text-sm">Sync Status</v-list-item-title>
                      </v-list-item>
                    </template>
                    <v-divider class="my-1"></v-divider>
                    <v-list-item @click="confirmDelete(letter)" v-iam-disabled="'notifications.templates.delete'">
                      <v-list-item-icon class="mr-3"><v-icon small
                          color="error">mdi-delete-outline</v-icon></v-list-item-icon>
                      <v-list-item-title class="text-red-600 font-medium text-sm">Delete</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>

            <div class="d-flex align-center gap-2 mb-3 flex-wrap">
              <v-chip x-small label v-if="letter.type" :color="getTypeStyle(letter.type).bg"
                :class="`${getTypeStyle(letter.type).color}--text font-bold px-2`" class="elevation-0">
                <v-icon left x-small :color="getTypeStyle(letter.type).color">{{
                  getTypeStyle(letter.type).icon
                  }}</v-icon>
                {{ letter.type || '-' }}
              </v-chip>
              <v-chip v-if="letter.type === 'WHATSAPP' && letter.whatsappStatus" x-small label
                :color="getWhatsAppStatusStyle(letter.whatsappStatus).bg" :class="`${getWhatsAppStatusStyle(letter.whatsappStatus).color
                  }--text font-bold px-2 ma-1`" class="elevation-0">
                <v-icon left x-small :color="getWhatsAppStatusStyle(letter.whatsappStatus).color">
                  {{ getWhatsAppStatusStyle(letter.whatsappStatus).icon }}
                </v-icon>
                Status: {{ letter.whatsappStatus }}
              </v-chip>
              <v-chip v-if="letter.type === 'SMS' && letter.smsStatus" x-small label
                :color="getWhatsAppStatusStyle(letter.smsStatus).bg" :class="`${getWhatsAppStatusStyle(letter.smsStatus).color
                  }--text font-bold px-2 ma-1`" class="elevation-0">
                <v-icon left x-small :color="getWhatsAppStatusStyle(letter.smsStatus).color">
                  {{ getWhatsAppStatusStyle(letter.smsStatus).icon }}
                </v-icon>
                Status: {{ letter.smsStatus }}
              </v-chip>
              <v-chip v-if="
                letter.type === 'SMS' &&
                (letter.smsTemplateId || (letter.metaData && letter.metaData.sqlTemplateId))
              " x-small label color="indigo lighten-5" class="indigo--text font-bold px-2 ma-1 elevation-0">
                <v-icon left x-small color="indigo">mdi-identifier</v-icon>
                DLT ID: {{ letter.smsTemplateId || letter.metaData.sqlTemplateId }}
              </v-chip>
              <v-chip v-if="letter.module" x-small label class="text-blue-grey darken-3 font-bold">
                {{ formatModuleName(letter.module) }}
              </v-chip>
              <v-chip v-if="letter.type === 'ATTACHMENT' && letter.attachmentType" x-small label color="purple darken-1"
                class="white--text font-bold px-2 elevation-0">
                <v-icon left x-small color="white">mdi-file-document-outline</v-icon>
                {{ letter.attachmentType }} FORMAT
              </v-chip>
              <template v-if="letter.type === 'EMAIL' && letter.attachments && letter.attachments.length">
                <v-chip v-for="(name, idx) in getAttachmentNames(letter.attachments)" :key="`attach-${idx}`" x-small
                  label color="teal lighten-5" class="teal--text font-bold px-2 ma-1 elevation-0">
                  <v-icon left x-small color="teal">mdi-paperclip</v-icon>
                  {{ name }}
                </v-chip>
              </template>
            </div>

            <!-- <div
              class="d-flex align-center text-[10px] text-gray-400 mb-3 font-medium uppercase tracking-tighter"
            >
              <v-icon size="10" class="mr-1">mdi-clock-outline</v-icon>
              Updated: {{ formatDate(letter.updatedAt || letter.createdAt) }}
            </div> -->

            <!-- <v-divider class="mb-3"></v-divider>

            <div class="d-flex align-center justify-space-between">
              <span
                class="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded uppercase"
                >Template</span
              >
              <div class="d-flex align-center text-[10px] text-gray-400">
                <v-icon size="10" class="mr-1">mdi-calendar-check</v-icon>
                {{ formatDate(letter.createdAt, true) }}
              </div>
            </div> -->
          </div>
        </v-card>
      </div>
    </div>

    <!-- Preview Dialog -->
    <v-dialog v-model="previewDialog" max-width="850px">
      <v-card class="p-4 overflow-hidden">
        <v-card-title class="py-4 px-6 d-flex justify-space-between border-b border-gray-100">
          <div class="d-flex flex-column">
            <h3 class="text-lg font-bold text-gray-800">
              {{ selectedLetter?.name || selectedLetter?.letterName }}
            </h3>
          </div>
          <v-btn icon @click="previewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-6 bg-gray-50 overflow-y-auto" style="max-height: 70vh">
          <div class="bg-white p-8 rounded-lg shadow-sm border border-gray-100 preview-content"
            v-html="selectedLetter?.content || selectedLetter?.letterContent"></div>

          <!-- WhatsApp Preview Buttons -->
          <div v-if="
            selectedLetter?.type === 'WHATSAPP' &&
            selectedLetter?.buttons &&
            selectedLetter.buttons.length
          " class="mt-4 flex flex-col gap-2">
            <div class="text-xs font-bold text-gray-400 uppercase tracking-wider">
              Template Buttons:
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
              <v-btn v-for="(btn, index) in selectedLetter.buttons" :key="index" outlined color="blue-grey lighten-1"
                class="normal-case rounded-lg text-sm bg-white" disabled>
                <v-icon left size="16" color="blue-grey darken-2">
                  {{
                    btn.type === 'PHONE_NUMBER'
                      ? 'mdi-phone'
                      : btn.type === 'URL'
                        ? 'mdi-open-in-new'
                        : 'mdi-reply'
                  }}
                </v-icon>
                {{ btn.text }}
              </v-btn>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="pt-4 border-t border-gray-100 bg-white">
          <v-spacer></v-spacer>
          <v-btn color="primary" depressed @click="previewDialog = false" class="normal-case rounded-lg px-8">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create/Edit Dialog - DESIGN UPDATED TO MATCH AddTemplate.vue -->
    <v-dialog v-model="dialog" persistent width="1200px">
      <v-card flat class="d-flex flex-column p-6" style="height: 90vh">
        <!-- Header -->
        <v-card-title class="py-4 px-6 d-flex justify-space-between border-b border-gray-200 top-header flex-shrink-0">
          <h3 class="form-title">
            {{ isEditing ? 'Edit Letter Template' : 'Create New Letter Template' }}
          </h3>
          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Scrollable Content Area -->
        <div class="overflow-y-auto flex-grow-1 p-6 bg-gray-50 p-2">
          <v-row>
            <v-col cols="12">
              <v-card outlined class="rounded-lg pa-4 bg-white border-gray-200">
                <v-row>
                  <v-col cols="12">
                    <v-text-field v-model="form.name" label="Template Name*" placeholder="e.g. welcome_letter" outlined
                      dense required class="rounded-lg" hide-details></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-select v-model="form.type" :items="['EMAIL', 'WHATSAPP', 'SMS', 'VOICE', 'ATTACHMENT']"
                      label="Template Type*" outlined dense class="rounded-lg" hide-details></v-select>
                  </v-col>
                </v-row>
                <v-row v-if="form.type === 'VOICE'">
                  <v-col cols="12" md="4">
                    <v-select v-model="form.language" :items="[
                      { text: 'Hindi (हिंदी)', value: 'hi-IN' },
                      { text: 'English (Indian Accent)', value: 'en-IN' },
                      { text: 'Marathi (मराठी)', value: 'mr-IN' },
                      { text: 'Gujarati (ગુજરાતી)', value: 'gu-IN' },
                      { text: 'Bengali (বাংলা)', value: 'bn-IN' },
                      { text: 'Tamil (தமிழ்)', value: 'ta-IN' },
                      { text: 'Telugu (తెలుగు)', value: 'te-IN' },
                      { text: 'Kannada (ಕನ್ನಡ)', value: 'kn-IN' }
                    ]" label="Speech Language" outlined dense class="rounded-lg" hide-details></v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select v-model="form.voiceGender" :items="[
                      { text: 'Female (महिला)', value: 'female' },
                      { text: 'Male (पुरुष)', value: 'male' }
                    ]" label="Voice Gender" outlined dense class="rounded-lg" hide-details></v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="form.viTemplateId" label="Vi CPaaS Template ID (Optional)"
                      placeholder="e.g. VI_OBD_12345" outlined dense class="rounded-lg" hide-details></v-text-field>
                  </v-col>
                </v-row>
                <v-row v-if="form.type === 'SMS'">
                  <v-col cols="12" md="4">
                    <v-text-field v-model="form.smsTemplateId" label="DLT Template ID"
                      placeholder="e.g. 120716993437302XXXX" outlined dense class="rounded-lg"
                      hide-details></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select v-model="form.smsServiceType" :items="[
                      { text: 'Service Implicit (SI)', value: 'SI' },
                      { text: 'Service Explicit (SE)', value: 'SE' },
                      { text: 'Transactional (T)', value: 'T' },
                      { text: 'Promotional (P)', value: 'P' }
                    ]" label="DLT Service Type" outlined dense class="rounded-lg" hide-details></v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="form.smsSender" label="Sender ID / Header"
                      placeholder="e.g. YESBNK (Defaults if empty)" outlined dense class="rounded-lg"
                      hide-details></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" v-if="form.type === 'ATTACHMENT'">
                    <v-select v-model="form.attachmentType" :items="['PDF', 'EXCEL', 'WORD']"
                      label="Attachment Formats*" chips small outlined dense class="rounded-lg" hide-details></v-select>
                  </v-col>
                  <v-col cols="12" v-if="form.type === 'WHATSAPP' && isEditing">
                    <v-text-field v-model="form.whatsappStatus" label="WhatsApp Meta Status" outlined dense readonly
                      disabled class="rounded-lg" hide-details></v-text-field>
                  </v-col>
                  <v-col cols="12" v-if="form.type === 'SMS' && isEditing">
                    <v-text-field v-model="form.smsStatus" label="SMS Quickberry Status" outlined dense readonly
                      disabled class="rounded-lg" hide-details></v-text-field>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>

            <v-col cols="12">
              <v-card outlined class="rounded-lg pa-4 bg-white border-gray-200 mt-2">
                <div class="d-flex justify-space-between align-center mb-0">
                  <h4 class="text-subtitle-1 font-bold text-gray-800">Available Parameters</h4>
                </div>

                <!-- Module Selection Dropdown -->
                <div class="mb-4">
                  <v-select v-model="selectedModule" :items="uniqueModules" label="Filter by Module" outlined dense
                    hide-details class="rounded-lg mt-2" prepend-inner-icon="mdi-filter-variant" item-text="text"
                    item-value="value"></v-select>
                </div>

                <div v-if="!selectedModule" class="text-sm text-gray-400 italic py-2">
                  Please select a module to see available parameters...
                </div>

                <!-- Global Parameters section (rendered as chips) -->
                <div v-if="globalSpecificParameters.length > 0" class="mb-4">
                  <div class="text-xs font-bold text-blue-600 uppercase tracking-wider mb-2 d-flex align-center">
                    <v-icon x-small color="blue" class="mr-1">mdi-earth</v-icon>
                    Global Parameters:
                  </div>
                  <div
                    class="parameter-grid-container pa-2 border-2 border-blue-100 rounded-lg bg-blue-50/20 overflow-y-auto"
                    style="max-height: 120px">
                    <div class="d-flex flex-wrap gap-2">
                      <v-chip v-for="opt in globalSpecificParameters" :key="opt.value" x-small outlined
                        color="blue darken-3" class="ma-1 parameter-clickable-chip bg-white"
                        @click="insertParameter(opt.value)">
                        {{ opt.text }}
                      </v-chip>
                    </div>
                  </div>
                </div>

                <div class="mb-4" v-if="selectedModule">
                  <v-autocomplete :items="parameterOptions" label="Search & Select Parameter" outlined dense
                    hide-details class="rounded-lg mt-2" prepend-inner-icon="mdi-magnify" @change="
                      (val) => {
                        if (val) {
                          insertParameter(val)
                          $nextTick(() => {
                            selectedParam = null
                          })
                        }
                      }
                    " v-model="selectedParam" placeholder="Search for a parameter..." clearable></v-autocomplete>

                  <!-- Selected Parameters Chips -->
                  <div v-if="selectedParameters.length > 0" class="mt-3 flex flex-wrap gap-2">
                    <v-chip v-for="(param, idx) in selectedParameters" :key="`sel-param-${idx}`" close size="small"
                      @click:close="removeSelectedParam(idx)" color="blue-grey lighten-5"
                      class="text-blue-grey text-xs font-medium">
                      {{ param }}
                    </v-chip>
                  </div>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" v-if="form.type === 'EMAIL' || form.type === 'WHATSAPP'">
              <v-text-field v-model="form.subject" label="Subject" outlined dense hide-details class="rounded-lg"
                prepend-inner-icon="mdi-format-title"></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-card outlined class="rounded-lg pa-4 bg-white border-gray-200 mt-2">
                <div v-if="form.type === 'SMS'">
                  <div class="d-flex justify-space-between align-center mb-3">
                    <label class="text-subtitle-1 font-bold text-gray-800 mb-0">Body Content (Plain Text)*</label>
                  </div>
                  <v-textarea ref="smsTextarea" v-model="form.content" outlined rows="12"
                    placeholder="Enter SMS plain text here. Use parameters on the right to insert placeholders..."
                    class="font-mono text-sm rounded-lg" hide-details></v-textarea>
                </div>
                <div v-else>
                  <div class="d-flex justify-space-between align-center mb-3">
                    <label class="text-subtitle-1 font-bold text-gray-800 mb-0">Body Content*</label>
                    <v-btn-toggle v-model="editorMode" mandatory dense class="rounded-lg">
                      <v-btn small value="editor">
                        <v-icon left x-small>mdi-format-text</v-icon>
                        Editor
                      </v-btn>
                      <v-btn small value="code">
                        <v-icon left x-small>mdi-code-tags</v-icon>
                        Code
                      </v-btn>
                    </v-btn-toggle>
                  </div>

                  <div v-if="dialog && editorMode === 'editor'"
                    class="editor-wrapper border rounded-lg overflow-hidden bg-white d-flex flex-column"
                    style="min-height: 550px">
                    <!-- Formatting Actions for visual contenteditable editor -->
                    <div class="grey lighten-4 border-b pa-2 d-flex align-center flex-wrap gap-2">
                      <v-btn icon x-small class="mr-1" @click="execEditorCommand('bold')" title="Bold">
                        <v-icon x-small>mdi-format-bold</v-icon>
                      </v-btn>
                      <v-btn icon x-small class="mr-1" @click="execEditorCommand('italic')" title="Italic">
                        <v-icon x-small>mdi-format-italic</v-icon>
                      </v-btn>
                      <v-btn icon x-small class="mr-1" @click="execEditorCommand('underline')" title="Underline">
                        <v-icon x-small>mdi-format-underline</v-icon>
                      </v-btn>
                      <v-btn icon x-small class="mr-1" @click="execEditorCommand('strikeThrough')"
                        title="Strike Through">
                        <v-icon x-small>mdi-format-strikethrough</v-icon>
                      </v-btn>
                      <v-divider vertical class="mx-2"></v-divider>
                      <v-btn icon x-small class="mr-1" @click="execEditorCommand('insertOrderedList')"
                        title="Ordered List">
                        <v-icon x-small>mdi-format-list-numbered</v-icon>
                      </v-btn>
                      <v-btn icon x-small class="mr-1" @click="execEditorCommand('insertUnorderedList')"
                        title="Bullet List">
                        <v-icon x-small>mdi-format-list-bulleted</v-icon>
                      </v-btn>
                      <v-divider vertical class="mx-2"></v-divider>
                      <v-btn icon x-small class="mr-1" @click="execEditorCommand('justifyLeft')" title="Align Left">
                        <v-icon x-small>mdi-format-align-left</v-icon>
                      </v-btn>
                      <v-btn icon x-small class="mr-1" @click="execEditorCommand('justifyCenter')" title="Align Center">
                        <v-icon x-small>mdi-format-align-center</v-icon>
                      </v-btn>
                      <v-btn icon x-small class="mr-1" @click="execEditorCommand('justifyRight')" title="Align Right">
                        <v-icon x-small>mdi-format-align-right</v-icon>
                      </v-btn>
                      <v-btn icon x-small class="mr-1" @click="execEditorCommand('justifyFull')" title="Justify Text">
                        <v-icon x-small>mdi-format-align-justify</v-icon>
                      </v-btn>
                      <v-btn icon x-small class="mr-1" @click="alignSpaceBetween()"
                        title="Space Between (Left & Right)">
                        <v-icon x-small>mdi-format-horizontal-align-center</v-icon>
                      </v-btn>
                      <v-divider vertical class="mx-2"></v-divider>
                      <!-- Line Height Selector -->
                      <v-menu offset-y dense>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn x-small outlined class="mr-1 text-[10px] normal-case" v-bind="attrs" v-on="on">
                            Line Height <v-icon x-small class="ml-1">mdi-chevron-down</v-icon>
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item v-for="lh in ['1.0', '1.2', '1.4', '1.6', '1.8', '2.0']" :key="lh"
                            @click="setEditorLineHeight(lh)">
                            <v-list-item-title class="text-caption">{{ lh }}</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                      <!-- Font Size Selector -->
                      <v-menu offset-y dense>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn x-small outlined class="mr-1 text-[10px] normal-case" v-bind="attrs" v-on="on">
                            Size <v-icon x-small class="ml-1">mdi-chevron-down</v-icon>
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item v-for="sz in [1, 2, 3, 4, 5, 6, 7]" :key="sz"
                            @click="execEditorCommand('fontSize', sz)">
                            <v-list-item-title class="text-caption">Size {{ sz }}</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                      <!-- Heading Selector -->
                      <v-menu offset-y dense>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn x-small outlined class="mr-1 text-[10px] normal-case" v-bind="attrs" v-on="on">
                            Headings <v-icon x-small class="ml-1">mdi-chevron-down</v-icon>
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item v-for="hd in ['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'P']" :key="hd" @click="
                            execEditorCommand('formatBlock', hd === 'P' ? '<p>' : `<${hd}>`)
                            ">
                            <v-list-item-title class="text-caption">{{ hd }}</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                      <!-- Color pickers -->
                      <v-menu offset-y :close-on-content-click="false" dense>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon x-small class="mr-1" v-bind="attrs" v-on="on" title="Font Color">
                            <v-icon x-small color="primary">mdi-format-color-text</v-icon>
                          </v-btn>
                        </template>
                        <v-card class="pa-2" flat>
                          <div class="d-flex flex-wrap gap-1" style="max-width: 140px">
                            <v-btn v-for="color in [
                              '#000000',
                              '#FF0000',
                              '#00FF00',
                              '#0000FF',
                              '#FFFF00',
                              '#FF00FF',
                              '#00FFFF',
                              '#888888'
                            ]" :key="color" icon x-small :style="{ backgroundColor: color }"
                              @click="execEditorCommand('foreColor', color)"></v-btn>
                          </div>
                        </v-card>
                      </v-menu>
                      <v-divider vertical class="mx-2"></v-divider>
                      <v-btn icon x-small class="mr-1" @click="insertTableAtCursor()" title="Insert 2x3 Table">
                        <v-icon x-small>mdi-table</v-icon>
                      </v-btn>
                      <v-btn icon x-small class="mr-1" @click="execEditorCommand('insertHorizontalRule')"
                        title="Divider Line">
                        <v-icon x-small>mdi-minus-box-outline</v-icon>
                      </v-btn>
                      <v-btn icon x-small class="mr-1" @click="execEditorCommand('removeFormat')"
                        title="Clear Formatting">
                        <v-icon x-small>mdi-format-clear</v-icon>
                      </v-btn>
                      <v-spacer></v-spacer>
                      <span class="text-[10px] text-gray-400 mr-2 uppercase font-medium">Visual WYSIWYG Editor</span>
                    </div>

                    <!-- Sandbox contenteditable iframe for reliable, native HTML manipulation -->
                    <div class="flex-grow pa-2" style="height: 500px">
                      <iframe ref="wysiwygIframe" style="width: 100%; height: 100%; border: none"
                        @load="initializeWysiwygIframe"></iframe>
                    </div>
                  </div>
                  <div v-else-if="dialog && editorMode === 'code'" class="code-editor-wrapper">
                    <v-textarea ref="codeTextarea" v-model="form.content" outlined rows="20"
                      placeholder="Paste your HTML code here..." class="font-mono text-sm rounded-lg"
                      hide-details></v-textarea>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" v-if="form.type === 'EMAIL' || form.type === 'WHATSAPP'">
              <v-autocomplete v-model="form.attachments" :items="availableAttachments" label="Add Attachments" multiple
                chips outlined dense class="rounded-lg" hide-details
                prepend-inner-icon="mdi-paperclip"></v-autocomplete>
            </v-col>

            <!-- WhatsApp Buttons Configurator -->
            <v-col cols="12" v-if="form.type === 'WHATSAPP'">
              <v-card outlined class="rounded-lg pa-4 bg-white border-gray-200 mt-2">
                <div class="d-flex justify-space-between align-center mb-3">
                  <div class="d-flex flex-column">
                    <span class="text-subtitle-1 font-bold text-gray-800">WhatsApp Template Buttons</span>
                    <span class="text-xs text-gray-500">Add up to 10 buttons to your template. Buttons allow customers
                      to
                      quickly
                      reply or navigate.</span>
                  </div>
                  <v-btn color="primary" outlined small class="normal-case rounded-lg" @click="addButton"
                    :disabled="form.buttons && form.buttons.length >= 10">
                    <v-icon left size="16">mdi-plus</v-icon>
                    Add Button
                  </v-btn>
                </div>

                <v-divider class="mb-4"></v-divider>

                <div v-if="!form.buttons || !form.buttons.length"
                  class="text-center py-6 border border-dashed rounded-lg bg-gray-50 text-gray-400 text-xs">
                  No buttons configured yet. Click "Add Button" to add a button.
                </div>

                <div v-else class="d-flex flex-column gap-3">
                  <div v-for="(btn, index) in form.buttons" :key="index"
                    class="d-flex align-center gap-3 border rounded-lg p-3 bg-gray-50/50">
                    <div class="text-xs font-bold text-gray-500 mr-2">#{{ index + 1 }}</div>

                    <v-select v-model="btn.type" :items="[
                      { text: 'Quick Reply', value: 'QUICK_REPLY' },
                      { text: 'Open Link (URL)', value: 'URL' },
                      { text: 'Call Phone', value: 'PHONE_NUMBER' }
                    ]" label="Button Type" outlined dense hide-details style="max-width: 180px"
                      class="rounded-lg bg-white" @change="onButtonTypeChange(index)"></v-select>

                    <v-text-field v-model="btn.text" label="Button Text" placeholder="e.g. Call Us" outlined dense
                      hide-details class="rounded-lg bg-white" maxlength="25"></v-text-field>

                    <v-text-field v-if="btn.type === 'URL'" v-model="btn.url" label="URL Link"
                      placeholder="https://example.com/promo" outlined dense hide-details
                      class="rounded-lg bg-white"></v-text-field>

                    <v-text-field v-if="btn.type === 'PHONE_NUMBER'" v-model="btn.phone_number" label="Phone Number"
                      placeholder="+15551234567" outlined dense hide-details class="rounded-lg bg-white"></v-text-field>

                    <div class="d-flex align-center gap-1 ml-auto">
                      <v-btn icon small @click="moveButton(index, -1)" :disabled="index === 0">
                        <v-icon size="18">mdi-arrow-up</v-icon>
                      </v-btn>
                      <v-btn icon small @click="moveButton(index, 1)" :disabled="index === form.buttons.length - 1">
                        <v-icon size="18">mdi-arrow-down</v-icon>
                      </v-btn>
                      <v-btn icon small color="error" @click="removeButton(index)">
                        <v-icon size="18">mdi-delete-outline</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Footer -->
        <div class="d-flex justify-end p-4 border-t border-gray-200 flex-shrink-0 bg-white shadow-top">
          <v-btn variant="outlined" color="primary" @click="dialog = false" class="mr-3 normal-case rounded-lg px-6">
            Cancel
          </v-btn>
          <v-btn color="primary" variant="flat" @click="saveLetter" :loading="saving"
            class="normal-case bg-blue-600 text-white rounded-lg px-8 shadow-sm">
            <v-icon left v-if="!saving">mdi-content-save</v-icon>
            {{ isEditing ? 'Update Template' : 'Save Template' }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card class="rounded-xl overflow-hidden">
        <v-card-title class="bg-red-50 text-red-800 py-4 px-6 font-bold">
          Delete Template?
        </v-card-title>
        <v-card-text class="pa-6 text-gray-600">
          Are you sure you want to delete
          <span class="font-bold text-gray-800">"{{ selectedLetter?.name || selectedLetter?.letterName }}"</span>? This
          action cannot be undone and any notifications linked to this letter will fail to
          attach it.
        </v-card-text>
        <v-card-actions class="pa-4 bg-gray-50">
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false" class="normal-case">Cancel</v-btn>
          <v-btn color="error" depressed @click="deleteLetter" class="normal-case rounded-lg px-6">
            Confirm Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- INSTANT TEST EMAIL DIALOG -->
    <v-dialog v-model="testEmailDialog" max-width="580px" persistent>
      <div class="bg-white rounded-lg shadow-xl overflow-hidden">
        <!-- Header -->
        <div class="px-6 py-4 bg-slate-100 border-b border-slate-200 flex justify-between items-center">
          <div class="font-bold text-sm text-slate-900 uppercase tracking-wider flex items-center gap-2">
            <v-icon color="teal darken-2" small>mdi-email-send</v-icon>
            Send Test Email
          </div>
          <v-btn icon small @click="testEmailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <!-- Body -->
        <div class="p-6">
          <div class="bg-teal-50 border border-teal-300 p-3.5 text-xs text-teal-950 flex items-start rounded-md mb-4">
            <v-icon small color="teal darken-2" class="mt-0.5 mr-2">mdi-information-outline</v-icon>
            <div>
              <strong>Instant Email Test:</strong> Renders this email template with your sample
              variable values and dispatches a test email immediately.
            </div>
          </div>

          <!-- Recipient Email -->
          <div class="mb-4">
            <v-text-field v-model="testEmailForm.email" label="Recipient Email Address *"
              placeholder="e.g. user@example.com" outlined dense prepend-inner-icon="mdi-email-outline"
              class="dialog-field-flat" hide-details="auto"></v-text-field>
          </div>

          <!-- Subject -->
          <div class="mb-4">
            <v-text-field v-model="testEmailForm.subject" label="Email Subject *"
              placeholder="e.g. Important Notification" outlined dense prepend-inner-icon="mdi-format-title"
              class="dialog-field-flat" hide-details="auto"></v-text-field>
          </div>

          <!-- Dynamic Variables Input Fields -->
          <div v-if="testEmailForm.variableKeys && testEmailForm.variableKeys.length"
            class="border border-slate-200 p-4 bg-slate-50 rounded-md">
            <div class="text-xs font-bold text-slate-800 uppercase tracking-wider mb-3 flex items-center">
              <v-icon x-small color="teal" class="mr-1.5">mdi-variable</v-icon>
              Fill Sample Values for Dynamic Variables
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div v-for="varKey in testEmailForm.variableKeys" :key="varKey" class="mb-2 sm:mb-0">
                <v-text-field v-model="testEmailForm.sampleVariables[varKey]" :label="`Value for {{${varKey}}}`"
                  :placeholder="varKey" dense outlined class="dialog-field-flat" hide-details="auto"
                  background-color="white"></v-text-field>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end">
          <v-btn text class="normal-case font-semibold mr-2" @click="testEmailDialog = false">Cancel</v-btn>
          <v-btn color="teal darken-2" class="white--text normal-case font-semibold px-5 rounded-lg"
            @click="executeTestEmail" :loading="testEmailLoading">
            <v-icon small left class="mr-1">mdi-send</v-icon>
            Send Test
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { getNotificationTriggerOptions } from '@/utils/workforce/constants'
import htmlToPdfmake from 'html-to-pdfmake'

export default {
  name: 'LetterTemplates',
  components: {},
  props: {
    search: {
      type: String,
      default: ''
    },
    typeFilter: {
      type: String,
      default: 'SMS'
    },
    forCampaign: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      letters: [],
      globalParameters: [],
      selectedModule: '',
      selectedParam: null,
      paramSearchQuery: '',
      selectedParameters: [],
      loading: false,
      saving: false,
      dialog: false,
      deleteDialog: false,
      previewDialog: false,
      testEmailDialog: false,
      testEmailLoading: false,
      testEmailForm: {
        templateId: '',
        email: '',
        subject: '',
        variableKeys: [],
        sampleVariables: {}
      },
      isEditing: false,
      selectedLetter: null,
      editorMode: 'editor',
      form: {
        templateCode: '',
        name: '',
        type: 'EMAIL',
        subject: '',
        content: '',
        contentJson: null,
        module: '',
        attachmentType: '',
        attachments: [],
        buttons: [],
        params: []
      },
      customToolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'],
        [{ header: 1 }, { header: 2 }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ direction: 'rtl' }],
        [{ size: ['small', false, 'large', 'huge'] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ color: [] }, { background: [] }],
        [{ font: [] }],
        [{ align: [] }],
        ['clean'],
        ['link', 'image', 'video']
      ]
    }
  },
  watch: {
    'form.content'(newVal) {
      this.extractParametersFromContent(newVal)
      this.updateContentJson(newVal)
      // Sync from Code editor to visual iframe if visual iframe is loaded
      if (this.editorMode === 'editor' && this.$refs.wysiwygIframe) {
        const doc =
          this.$refs.wysiwygIframe.contentDocument ||
          this.$refs.wysiwygIframe.contentWindow.document
        if (doc && doc.body && doc.body.innerHTML !== newVal) {
          doc.body.innerHTML = newVal || ''
        }
      }
    },
    selectedModule(newVal) {
      this.form.module = newVal
    },
    editorMode(newVal) {
      if (newVal === 'editor') {
        this.$nextTick(() => {
          this.initializeWysiwygIframe()
        })
      }
    }
  },
  computed: {
    availableAttachments() {
      return (this.letters || [])
        .filter((l) => l.type === 'ATTACHMENT')
        .map((l) => ({
          text: l.name,
          value: l._id
        }))
    },
    totalPages() {
      return 0
    },
    filteredLetters() {
      let result = this.letters.filter((l) => {
        const isCamp = Boolean(l.forCampaign)
        return isCamp === Boolean(this.forCampaign)
      })
      if (this.typeFilter) {
        result = result.filter(
          (l) => l.type && l.type.toUpperCase() === this.typeFilter.toUpperCase()
        )
      }
      if (!this.search) return result
      const s = this.search.toLowerCase()
      return result.filter(
        (l) =>
          (l.name && l.name.toLowerCase().includes(s)) ||
          (l.letterName && l.letterName.toLowerCase().includes(s)) ||
          (l.content && l.content.toLowerCase().includes(s)) ||
          (l.letterContent && l.letterContent.toLowerCase().includes(s))
      )
    },
    parameterOptions() {
      if (!this.selectedModule) return []
      if (this.selectedModule === 'other') {
        return [
          { text: 'Recipient Name', value: 'name' },
          { text: 'Recipient Mobile/Number', value: 'mobile' },
          { text: 'Recipient Email', value: 'email' }
        ]
      }
      let params = this.globalParameters.filter(
        (p) => p.module && p.module.toLowerCase() !== 'global'
      )
      params = params.filter((p) => p.module === this.selectedModule)

      if (params.length > 0) {
        return params.map((p) => ({
          text: p.parameterDescription || p.parameterKey,
          value: p.parameterKey
        }))
      }
      return []
    },
    uniqueModules() {
      const modules = new Set(
        this.globalParameters
          .filter((p) => p.module && p.module.toLowerCase() !== 'global')
          .map((p) => p.module)
      )
      const list = Array.from(modules)
        .sort()
        .map((m) => ({
          text: m.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
          value: m
        }))
      list.push({ text: 'Other', value: 'other' })
      return list
    },
    globalSpecificParameters() {
      return this.globalParameters
        .filter((p) => p.module && p.module.toLowerCase() === 'global')
        .map((p) => ({
          text: p.parameterDescription || p.parameterKey,
          value: p.parameterKey
        }))
    }
  },
  async mounted() {
    await Promise.all([this.fetchLetters(), this.fetchParameters()])
  },
  methods: {
    getWhatsAppStatusStyle(status) {
      switch (status) {
        case 'APPROVED':
          return {
            bg: 'green lighten-5',
            color: 'green darken-4',
            icon: 'mdi-checkbox-marked-circle-outline'
          }
        case 'REJECTED':
          return { bg: 'red lighten-5', color: 'red darken-4', icon: 'mdi-close-circle-outline' }
        case 'PENDING':
          return { bg: 'orange lighten-5', color: 'orange darken-4', icon: 'mdi-clock-outline' }
        default:
          return { bg: 'grey lighten-4', color: 'grey darken-3', icon: 'mdi-help-circle-outline' }
      }
    },
    async submitToMeta(letter) {
      try {
        this.loading = true
        const response = await this.$axios.post(`/letter-templates/${letter._id}/submit-whatsapp`)
        this.$toast.success('Submitted to Meta for approval successfully')
        await this.fetchLetters()
      } catch (error) {
        console.error('Error submitting WhatsApp template to Meta:', error)
        const msg = error.response?.data?.message || 'Failed to submit template to Meta'
        this.$toast.error(msg)
      } finally {
        this.loading = false
      }
    },
    async syncMetaStatus(letter) {
      try {
        this.loading = true
        const response = await this.$axios.get(`/letter-templates/${letter._id}/sync-whatsapp`)
        this.$toast.success(
          `Status synchronized successfully: ${response.data.data.whatsappStatus}`
        )
        await this.fetchLetters()
      } catch (error) {
        console.error('Error syncing template status from Meta:', error)
        const msg = error.response?.data?.message || 'Failed to sync status from Meta'
        this.$toast.error(msg)
      } finally {
        this.loading = false
      }
    },
    async submitToQuickberry(letter) {
      try {
        this.loading = true
        const response = await this.$axios.post(`/letter-templates/${letter._id}/submit-sms`)
        this.$toast.success('Submitted to Quickberry for approval successfully')
        await this.fetchLetters()
      } catch (error) {
        console.error('Error submitting SMS template to Quickberry:', error)
        const msg = error.response?.data?.message || 'Failed to submit template to Quickberry'
        this.$toast.error(msg)
      } finally {
        this.loading = false
      }
    },
    async syncQuickberryStatus(letter) {
      try {
        this.loading = true
        const response = await this.$axios.get(`/letter-templates/${letter._id}/sync-sms`)
        this.$toast.success(`Status synchronized successfully: ${response.data.data.smsStatus}`)
        await this.fetchLetters()
      } catch (error) {
        console.error('Error syncing template status from Quickberry:', error)
        const msg = error.response?.data?.message || 'Failed to sync status from Quickberry'
        this.$toast.error(msg)
      } finally {
        this.loading = false
      }
    },
    async fetchLetters() {
      try {
        this.loading = true
        const response = await this.$axios.get('/letter-templates?limit=*')
        const data = response.data?.data || response.data?.letters || response.data || []
        this.letters = Array.isArray(data) ? data : []
      } catch (error) {
        console.error('Error fetching letters:', error)
        this.$toast.error('Failed to fetch letter templates')
      } finally {
        this.loading = false
      }
    },
    async fetchParameters() {
      try {
        const response = await this.$axios.get('/template-params-mapping')
        const rawData = response.data?.data || response.data || {}

        // Transform the nested mapping into a flat array format for the UI
        const flattenedParams = []

        Object.entries(rawData).forEach(([moduleKey, moduleData]) => {
          const parameters = moduleData.dataParameters || {}

          Object.entries(parameters).forEach(([subModuleKey, params]) => {
            Object.entries(params).forEach(([paramKey, paramData]) => {
              flattenedParams.push({
                module: moduleKey, // Use snake_case key (e.g. loan_management)
                subModule: subModuleKey,
                parameterKey: paramKey,
                parameterDescription: paramData.displayName || paramKey,
                path: paramData.path
              })
            })
          })
        })

        this.globalParameters = flattenedParams
      } catch (error) {
        console.error('Error fetching template parameters:', error)
      }
    },
    openPreview(letter) {
      this.selectedLetter = letter
      this.previewDialog = true
    },
    previewLetter(letter) {
      this.selectedLetter = letter
      this.previewDialog = true
    },
    insertParameter(param) {
      if (param) {
        const variableText = `{{${param}}}`

        if (this.form.type === 'SMS') {
          const textarea = this.$refs.smsTextarea?.$refs.input
          if (textarea) {
            const startPos = textarea.selectionStart
            const endPos = textarea.selectionEnd
            this.form.content =
              this.form.content.substring(0, startPos) +
              variableText +
              this.form.content.substring(endPos, this.form.content.length)

            this.$nextTick(() => {
              textarea.focus()
              const newPos = startPos + variableText.length
              textarea.setSelectionRange(newPos, newPos)
            })
          } else {
            this.form.content += variableText
          }
        } else if (this.editorMode === 'code') {
          // Precise cursor injection for raw HTML editing view
          const textarea = this.$refs.codeTextarea?.$refs.input
          if (textarea) {
            const startPos = textarea.selectionStart
            const endPos = textarea.selectionEnd
            this.form.content =
              this.form.content.substring(0, startPos) +
              variableText +
              this.form.content.substring(endPos, this.form.content.length)

            this.$nextTick(() => {
              textarea.focus()
              const newPos = startPos + variableText.length
              textarea.setSelectionRange(newPos, newPos)
            })
          } else {
            this.form.content += variableText
          }
        } else {
          // Visual WYSIWYG editor variable insertion
          const iframe = this.$refs.wysiwygIframe
          if (iframe) {
            const win = iframe.contentWindow
            const doc = iframe.contentDocument || win.document
            win.focus()

            // Insert variable at cursor selection inside the iframe
            const sel = win.getSelection()
            if (sel.rangeCount) {
              const range = sel.getRangeAt(0)
              range.deleteContents()
              const textNode = doc.createTextNode(variableText)
              range.insertNode(textNode)

              // Move cursor to the end of inserted variable
              range.setStartAfter(textNode)
              range.setEndAfter(textNode)
              sel.removeAllRanges()
              sel.addRange(range)

              // Sync updated HTML back to form.content
              this.form.content = doc.body.innerHTML
            } else {
              this.form.content += variableText
            }
          } else {
            this.form.content += variableText
          }
        }

        this.$toast.info(`Inserted {{${param}}}`)
      }
    },
    removeSelectedParam(index) {
      const param = this.selectedParameters[index]
      if (param) {
        const variableText = `{{${param}}}`
        // Remove all instances of the parameter from the content
        if (this.form.content) {
          this.form.content = this.form.content.split(variableText).join('')
        }
      }
      this.selectedParameters.splice(index, 1)
    },
    openCreateDialog() {
      this.isEditing = false
      this.selectedLetter = null
      this.form = {
        templateCode: '',
        name: '',
        type: this.typeFilter || 'EMAIL',
        forCampaign: Boolean(this.forCampaign),
        subject: '',
        content: '',
        contentJson: null,
        module: 'All',
        attachmentType: '',
        attachments: [],
        whatsappLangCode: 'en',
        whatsappStatus: 'DRAFT',
        smsStatus: 'DRAFT',
        smsTemplateId: '',
        smsServiceType: 'T',
        smsSender: '',
        buttons: [],
        params: [],
        language: 'hi-IN',
        voiceGender: 'female',
        viTemplateId: ''
      }
      this.dialog = true
    },
    editLetter(letter) {
      this.isEditing = true
      this.selectedLetter = letter
      // Set the active filter to match the template's module
      this.selectedModule = letter.module || ''

      this.form = {
        templateCode: letter.templateCode || '',
        name: letter.name || letter.letterName || '',
        type: letter.type || 'EMAIL',
        forCampaign:
          letter.forCampaign !== undefined
            ? Boolean(letter.forCampaign)
            : Boolean(this.forCampaign),
        subject: letter.subject || '',
        content: letter.content || letter.letterContent || '',
        contentJson: letter.contentJson || null,
        module: letter.module || '',
        attachmentType: letter.attachmentType || '',
        attachments: letter.attachments || [],
        whatsappLangCode: letter.whatsappLangCode || 'en',
        whatsappStatus: letter.whatsappStatus || 'DRAFT',
        smsStatus: letter.smsStatus || 'DRAFT',
        smsTemplateId:
          letter.smsTemplateId || (letter.metaData && letter.metaData.sqlTemplateId) || '',
        smsServiceType: letter.smsServiceType || 'T',
        smsSender: letter.smsSender || '',
        buttons: letter.buttons ? JSON.parse(JSON.stringify(letter.buttons)) : [],
        params: letter.params || [],
        language: letter.language || 'hi-IN',
        voiceGender: letter.voiceGender || 'female',
        viTemplateId: letter.viTemplateId || ''
      }

      // Auto-extract parameters from content for the chips
      this.extractParametersFromContent(this.form.content)

      // Automatically switch to code mode if it looks like an HTML layout document
      const lowerContent = (this.form.content || '').toLowerCase()
      if (
        lowerContent.includes('<html') ||
        lowerContent.includes('<table') ||
        lowerContent.includes('<div') ||
        lowerContent.includes('<style') ||
        lowerContent.includes('style=')
      ) {
        this.editorMode = 'code'
      } else {
        this.editorMode = 'editor'
      }

      this.dialog = true
    },
    extractParametersFromContent(content) {
      if (!content) {
        this.selectedParameters = []
        this.form.params = []
        return
      }

      // Support both {{param}} and #param#
      const curlyRegex = /\{\{([^}]+)\}\}/g
      const hashRegex = /#([a-zA-Z0-9_]+)#/g

      const found = []
      let match

      // Extract {{...}}
      while ((match = curlyRegex.exec(content)) !== null) {
        if (!found.includes(match[1])) found.push(match[1])
      }

      // Extract #...#
      while ((match = hashRegex.exec(content)) !== null) {
        if (!found.includes(match[1])) found.push(match[1])
      }

      this.selectedParameters = found
      this.form.params = found
    },
    updateContentJson(content) {
      if (!content) {
        this.form.contentJson = null
        return
      }
      try {
        // html-to-pdfmake converts HTML into pdfmake JSON structure using the browser's DOM parser
        this.form.contentJson = htmlToPdfmake(content, {
          tableAutoSize: true,
          removeExtraBlanks: true,
          defaultStyles: {
            b: { bold: true },
            strong: { bold: true },
            u: { decoration: 'underline' },
            em: { italics: true },
            i: { italics: true },
            s: { decoration: 'lineThrough' },
            h1: { fontSize: 24, bold: true, marginBottom: 5 },
            h2: { fontSize: 20, bold: true, marginBottom: 5 },
            h3: { fontSize: 18, bold: true, marginBottom: 4 },
            h4: { fontSize: 16, bold: true, marginBottom: 4 },
            h5: { fontSize: 14, bold: true, marginBottom: 3 },
            h6: { fontSize: 12, bold: true, marginBottom: 3 },
            a: { color: '#1a0dab', decoration: 'underline' },
            th: { bold: true, fillColor: '#f2f2f2' },
            p: { margin: [0, 2, 0, 2] },
            table: { margin: [0, 5, 0, 5] },
            td: { margin: [0, 2, 0, 2] },
            li: { margin: [0, 1, 0, 1] }
          },
          customHeaders: {
            // Parses alignment configurations (center/right) inside inline styles or classes
            customAttr: function (node, attr) {
              const styles = node.getAttribute('style') || ''
              const classes = node.getAttribute('class') || ''
              if (styles.includes('text-align: center') || classes.includes('ql-align-center')) {
                attr.alignment = 'center'
              } else if (
                styles.includes('text-align: right') ||
                classes.includes('ql-align-right')
              ) {
                attr.alignment = 'right'
              } else if (
                styles.includes('text-align: justify') ||
                classes.includes('ql-align-justify')
              ) {
                attr.alignment = 'justify'
              }
              return attr
            }
          }
        })
      } catch (err) {
        console.error('Error generating contentJson from HTML:', err)
        this.form.contentJson = [{ text: content.replace(/<[^>]*>/g, '') }]
      }
    },
    addButton() {
      if (!this.form.buttons) {
        this.$set(this.form, 'buttons', [])
      }
      if (this.form.buttons.length < 10) {
        this.form.buttons.push({
          type: 'QUICK_REPLY',
          text: '',
          url: '',
          phone_number: ''
        })
      }
    },
    removeButton(index) {
      this.form.buttons.splice(index, 1)
    },
    moveButton(index, direction) {
      const targetIndex = index + direction
      if (targetIndex >= 0 && targetIndex < this.form.buttons.length) {
        const temp = this.form.buttons[index]
        this.$set(this.form.buttons, index, this.form.buttons[targetIndex])
        this.$set(this.form.buttons, targetIndex, temp)
      }
    },
    onButtonTypeChange(index) {
      const btn = this.form.buttons[index]
      if (btn.type === 'QUICK_REPLY') {
        btn.url = ''
        btn.phone_number = ''
      } else if (btn.type === 'URL') {
        btn.phone_number = ''
      } else if (btn.type === 'PHONE_NUMBER') {
        btn.url = ''
      }
    },
    getTypeStyle(type) {
      switch (type) {
        case 'SMS':
          return { bg: 'amber lighten-5', color: 'amber darken-4', icon: 'mdi-message-processing' }
        case 'WHATSAPP':
          return { bg: 'green lighten-5', color: 'green darken-4', icon: 'mdi-whatsapp' }
        case 'EMAIL':
          return { bg: 'blue lighten-5', color: 'blue darken-4', icon: 'mdi-email-outline' }
        case 'VOICE':
          return { bg: 'purple lighten-5', color: 'purple darken-4', icon: 'mdi-phone-in-talk' }
        case 'ATTACHMENT':
          return {
            bg: 'deep-purple lighten-5',
            color: 'deep-purple darken-4',
            icon: 'mdi-paperclip'
          }
        default:
          return { bg: 'grey lighten-4', color: 'grey darken-3', icon: 'mdi-file-outline' }
      }
    },
    stripHtml(html) {
      if (!html) return ''
      // Replace <br> and <p> closing tags with newlines to preserve structure
      let text = html.replace(/<br\s*\/?>/gi, '\n')
      text = text.replace(/<\/p>|<\/div>/gi, '\n')
      // Strip remaining HTML tags
      text = text.replace(/<[^>]+>/g, '')
      // Decode HTML entities
      const doc = new DOMParser().parseFromString(text, 'text/html')
      const cleaned = doc.body.textContent || doc.body.innerText || text
      return cleaned.trim()
    },
    initializeWysiwygIframe() {
      const iframe = this.$refs.wysiwygIframe
      if (!iframe) return

      const win = iframe.contentWindow
      const doc = iframe.contentDocument || win.document

      // Write basic document structure with contenteditable body
      doc.open()
      doc.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            body {
              margin: 0;
              padding: 20px;
              min-height: 460px;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
              font-size: 15px;
              line-height: 1.6;
              color: #222222;
              background-color: #ffffff;
              outline: none;
            }
            /* Visual indicator for tables */
            table {
              border-collapse: collapse;
              width: 100%;
              margin: 10px 0;
            }
            td, th {
              border: 1px dashed #cccccc;
              padding: 8px;
              min-width: 30px;
            }
            /* Focused outline border */
            body:focus {
              box-shadow: inset 0 0 4px rgba(66, 153, 225, 0.5);
            }
          </style>
        </head>
        <body contenteditable="true">
          ${this.form.content || ''}
        </body>
        </html>
      `)
      doc.close()

      // Event listener to sync edits from Editor to Code representation (form.content)
      const syncChanges = () => {
        this.form.content = doc.body.innerHTML
      }
      doc.body.addEventListener('input', syncChanges)
      doc.body.addEventListener('keyup', syncChanges)
      doc.body.addEventListener('paste', syncChanges)
      doc.body.addEventListener('blur', syncChanges)
    },
    execEditorCommand(command, arg = null) {
      const iframe = this.$refs.wysiwygIframe
      if (!iframe) return
      const win = iframe.contentWindow
      const doc = iframe.contentDocument || win.document

      win.focus()
      doc.execCommand(command, false, arg)
      // Sync changes back to form
      this.form.content = doc.body.innerHTML
    },
    insertTableAtCursor() {
      const tableHtml = `
        <table style="width: 100%; border-collapse: collapse; margin: 10px 0;">
          <tbody>
            <tr>
              <td style="border: 1px solid #999999; padding: 8px;">Col 1 Row 1</td>
              <td style="border: 1px solid #999999; padding: 8px;">Col 2 Row 1</td>
              <td style="border: 1px solid #999999; padding: 8px;">Col 3 Row 1</td>
            </tr>
            <tr>
              <td style="border: 1px solid #999999; padding: 8px;">Col 1 Row 2</td>
              <td style="border: 1px solid #999999; padding: 8px;">Col 2 Row 2</td>
              <td style="border: 1px solid #999999; padding: 8px;">Col 3 Row 2</td>
            </tr>
          </tbody>
        </table>
      `
      this.execEditorCommand('insertHTML', tableHtml)
    },
    alignSpaceBetween() {
      const flexHtml = `<p style="display: flex; justify-content: space-between; align-items: center; margin: 0 0 10px 0;"><strong>Left Item</strong><strong>Right Item</strong></p>`
      this.execEditorCommand('insertHTML', flexHtml)
    },
    setEditorLineHeight(lh) {
      const iframe = this.$refs.wysiwygIframe
      if (!iframe) return
      const win = iframe.contentWindow
      const doc = iframe.contentDocument || win.document

      win.focus()
      const sel = win.getSelection()
      if (sel && sel.rangeCount > 0) {
        let container = sel.getRangeAt(0).commonAncestorContainer
        if (container.nodeType === 3) container = container.parentNode
        if (container && container !== doc.body) {
          container.style.lineHeight = lh
        } else {
          doc.body.style.lineHeight = lh
        }
      } else {
        doc.body.style.lineHeight = lh
      }
      this.form.content = doc.body.innerHTML
      this.updateContentJson(this.form.content)
    },
    async saveLetter() {
      if (!this.form.name) {
        this.$toast.error('Template name is required')
        return
      }

      if (this.form.content && this.form.type === 'SMS') {
        this.form.content = this.stripHtml(this.form.content)
      }

      if (this.form.type === 'SMS' && !this.form.smsTemplateId) {
        this.$toast.error('DLT Template ID is required for SMS templates')
        return
      }

      if (this.form.type === 'WHATSAPP' && this.form.buttons && this.form.buttons.length) {
        for (let i = 0; i < this.form.buttons.length; i++) {
          const btn = this.form.buttons[i]
          if (!btn.text || !btn.text.trim()) {
            this.$toast.error(`Button #${i + 1} text is required`)
            return
          }
          if (btn.type === 'URL' && (!btn.url || !btn.url.trim())) {
            this.$toast.error(`Button #${i + 1} URL is required`)
            return
          }
          if (btn.type === 'PHONE_NUMBER' && (!btn.phone_number || !btn.phone_number.trim())) {
            this.$toast.error(`Button #${i + 1} phone number is required`)
            return
          }
        }
      }

      // Ensure module is snake_case (backup check)
      if (this.selectedModule && this.selectedModule !== 'Global') {
        this.form.module = this.selectedModule.toLowerCase().replace(/\s+/g, '_')
      } else if (this.selectedModule === 'Global') {
        this.form.module = 'global'
      }

      try {
        this.saving = true
        if (this.isEditing) {
          await this.$axios.patch(`/letter-templates/${this.selectedLetter._id}`, this.form)
        } else {
          await this.$axios.post('/letter-templates', this.form)
        }

        this.$toast.success('Template saved successfully')
        this.dialog = false
        await this.fetchLetters()
      } catch (error) {
        console.error('Error saving letter:', error)
        const msg = error.response?.data?.message || 'Failed to save letter template'
        this.$toast.error(msg)
      } finally {
        this.saving = false
      }
    },
    triggerTestCall(letter) {
      this.$emit('test-call', letter)
    },
    triggerTestEmail(letter) {
      const subject = letter.subject || ''
      const content = letter.content || letter.letterContent || ''
      const allText = subject + ' ' + content

      const regex = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g
      const varKeys = []
      let match
      while ((match = regex.exec(allText)) !== null) {
        const k = match[1].trim()
        if (!varKeys.includes(k)) varKeys.push(k)
      }

      const sampleVals = {}
      varKeys.forEach((k) => {
        if (k.toLowerCase().includes('name')) sampleVals[k] = 'Gaurav'
        else if (k.toLowerCase().includes('amount') || k.toLowerCase().includes('emi'))
          sampleVals[k] = '2500'
        else if (k.toLowerCase().includes('date'))
          sampleVals[k] = new Date().toLocaleDateString('en-IN')
        else sampleVals[k] = `Sample ${k}`
      })

      this.testEmailForm = {
        templateId: letter._id,
        email: this.$store?.state?.auth?.user?.email || '',
        subject: subject || 'Test Notification',
        variableKeys: varKeys,
        sampleVariables: sampleVals
      }
      this.testEmailDialog = true
    },
    async executeTestEmail() {
      if (!this.testEmailForm.email || !this.testEmailForm.email.includes('@')) {
        this.$toast.error('Please enter a valid recipient email address')
        return
      }

      this.testEmailLoading = true
      try {
        const payload = {
          email: this.testEmailForm.email,
          subject: this.testEmailForm.subject,
          sampleVariables: this.testEmailForm.sampleVariables
        }
        let response
        try {
          response = await this.$axios.post(
            `/letter-templates/${this.testEmailForm.templateId}/test-email`,
            payload
          )
        } catch (postErr) {
          // If the dedicated test-email route returned 404 on current server deployment, fallback to the existing manual-bulk endpoint
          if (postErr.response && postErr.response.status === 404) {
            response = await this.$axios.post('/notifications/manual-bulk', {
              channel: 'EMAIL',
              templateId: this.testEmailForm.templateId,
              manualEmails: [this.testEmailForm.email],
              useCase: 'other',
              sampleVariables: this.testEmailForm.sampleVariables
            })
          } else {
            throw postErr
          }
        }

        if (
          response &&
          (response.data?.success || response.data?.message || response.status === 200)
        ) {
          this.$toast.success(`Test email dispatched successfully to ${this.testEmailForm.email}`)
          this.testEmailDialog = false
        } else {
          this.$toast.error(response?.data?.message || 'Failed to send test email')
        }
      } catch (err) {
        const msg = err.response?.data?.message || 'Error sending test email'
        this.$toast.error(msg)
      } finally {
        this.testEmailLoading = false
      }
    },
    confirmDelete(letter) {
      this.selectedLetter = letter
      this.deleteDialog = true
    },
    async deleteLetter() {
      try {
        await this.$axios.delete(`/letter-templates/${this.selectedLetter._id}`)
        this.$toast.success('Letter template deleted')
        this.deleteDialog = false
        await this.fetchLetters()
      } catch (error) {
        console.error('Error deleting letter:', error)
        this.$toast.error('Failed to delete letter template')
      }
    },
    formatDate(dateStr, dateOnly = false) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      if (dateOnly) return date.toLocaleDateString('en-IN')

      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    formatModuleName(name) {
      if (!name || name === 'All') return name
      return name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    },
    getAttachmentNames(attachmentIds) {
      if (!attachmentIds || !Array.isArray(attachmentIds)) return []
      return attachmentIds
        .map((id) => {
          const found = this.letters.find((l) => l._id === id)
          return found ? found.name : null
        })
        .filter(Boolean)
    }
  }
}
</script>

<style scoped>
.letter-templates-container {
  padding: 24px;
  background-color: #f9fafb;
  min-height: 80vh;
}

.letter-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background-color: white;
  border: 1px solid #d1d5db;
}

/* .letter-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 30px -10px rgba(0, 0, 0, 0.1) !important;
} */

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.form-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #21427d;
  margin: 0;
}

.top-header {
  position: sticky;
  top: 0;
  background-color: #fff;
  z-index: 50;
}

.shadow-top {
  box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.05);
}

.param-chip {
  transition: all 0.2s ease;
  border: 1px solid #d1d5db;
}

.param-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.quill-editor {
  height: 400px;
}

.editor-wrapper :deep(.ql-container) {
  height: 350px !important;
}

.editor-wrapper :deep(.ql-editor) {
  min-height: 350px !important;
}

.editor-wrapper {
  background: white;
}

/* Customizing scrollbar for scrollable text area */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 10px;
}
</style>
